<template>
  <b-modal
    ref="modalThietBiUser"
    title="Quản lý thiết bị xác thực 2 bước"
    size="lg"
    centered
    hide-footer
  >
    <b-table
      :items="devices"
      :fields="fields"
      responsive
      bordered
      show-empty
      empty-text="Người dùng chưa đăng ký thiết bị nào."
      head-variant="light"
      class="table-thiet-bi"
    >
      <template #cell(state)="row">
        <b-badge :variant="stateVariant(row.item.state)">
          {{ stateLabel(row.item.state) }}
        </b-badge>
      </template>
      <template #cell(isPrimary)="row">
        <!-- Nhãn vai trò đầy đủ đồng bộ màn self QLVB (ModalQuanLyThietBiCuaToi.vue):
             primary="Thiết bị chính"; non-primary="Thiết bị dự phòng". -->
        <span v-if="row.item.isPrimary" class="text-success font-weight-bold text-nowrap"
          >✔ Thiết bị chính</span
        >
        <span v-else class="text-muted text-nowrap">Thiết bị dự phòng</span>
      </template>
      <template #cell(lanDungCuoi)="row">
        {{ formatDate(row.item.lanDungCuoi) }}
      </template>
      <template #cell(thaoTac)="row">
        <!-- Active (1) -> Khoá + Hủy; Locked (2) -> Mở khoá + Hủy; ChoKichHoat (0)/DaHuy (3) -> không thao tác.
             flex + gap: giãn khoảng cách 2 nút Khoá/Mở-khoá và Hủy để không dính nhau, không wrap khi cột hẹp. -->
        <div class="d-flex flex-nowrap align-items-center thao-tac-buttons">
          <b-button
            v-if="row.item.state === 1"
            size="sm"
            variant="danger"
            class="text-nowrap"
            @click="onLock(row.item)"
            >Khoá</b-button
          >
          <b-button
            v-else-if="row.item.state === 2"
            size="sm"
            variant="success"
            class="text-nowrap"
            @click="onUnlock(row.item)"
            >Mở khoá</b-button
          >
          <!-- UC7.5: hủy thiết bị (soft-state DaHuyKichHoat). Chỉ cho TB Active/Locked, không cho TB đã hủy. -->
          <b-button
            v-if="row.item.state === 1 || row.item.state === 2"
            size="sm"
            variant="outline-danger"
            class="text-nowrap"
            @click="onRevoke(row.item)"
            >Hủy thiết bị</b-button
          >
        </div>
      </template>
    </b-table>

    <!-- Sub-modal step-up OTP admin: dùng chung cho cả Khoá lẫn Mở khoá. -->
    <b-modal
      ref="modalStepUp"
      :title="stepUpTitle"
      centered
      no-close-on-backdrop
      no-close-on-esc
      @hidden="onStepUpHidden"
    >
      <div class="step-up-otp otp-scope">
        <p class="mb-1">
          Nhập mã OTP hiện tại của <b>chính bạn</b> để xác nhận thao tác (bạn phải
          đang bật xác thực 2 bước).
        </p>
        <div class="mb-1 font-weight-bold">Mã xác thực của bạn</div>
        <b-form-input
          v-otp-autofocus
          ref="inputOtp"
          v-model="otp"
          type="text"
          inputmode="numeric"
          maxlength="6"
          autocomplete="one-time-code"
          pattern="[0-9]*"
          class="input"
          placeholder="Mã OTP 6 số"
          @keyup.enter="confirmStepUp"
        />
      </div>
      <template v-slot:modal-footer>
        <div class="w-100 text-right">
          <base-button
            button="Xác nhận"
            :disabled="!otp"
            @click="confirmStepUp"
          />
          <b-button
            variant="outline-primary"
            class="ml-1"
            @click="$refs.modalStepUp.hide()"
            >Đóng</b-button
          >
        </div>
      </template>
    </b-modal>

    <BaseLoading :is-loading="isLoading" />
  </b-modal>
</template>

<script>
import BaseLoading from "@/components/common/BaseLoading";
// Phase 10 UC12: admin xem + khoá/mở thiết bị OTP của user khác.
// Khoá = đổi State Active->Locked (verify per-device sau đó tự chặn ở BE); mở = Locked->Active.
// STEP-UP: admin nhập OTP CHÍNH MÌNH -> gửi { userId (target), deviceId (target), twoFactorCode }.
// KHÔNG gửi adminDeviceId/authorizerDeviceId: getOrCreateDeviceId (localStorage browser) KHÔNG khớp
// Device.DeviceId (app QLVB sinh khi enroll QR) -> BE RunOtpSecurityPipelineAsync trả "device-invalid".
// Bỏ trường -> BE fan-out VerifyWithActiveDevicesFallbackAsync (WebAuthn multi-credential, TwoFactorAuthenticatorService
// line 118 P2 QR handoff Bug 8). Đồng bộ ModalResetOtpConfirm cùng repo.
// Khoá device chính (isPrimary) -> cảnh báo trước (user sẽ mất đăng nhập bằng thiết bị đó).
export default {
  name: "ModalQuanLyThietBiUser",
  components: { BaseLoading },
  data() {
    return {
      targetUserId: null,
      devices: [],
      isLoading: false,
      // Ngữ cảnh thao tác đang chờ step-up: { action: 'lock'|'unlock', deviceId }.
      pending: null,
      otp: null,
      fields: [
        { key: "tenThietBi", label: "Tên thiết bị" },
        { key: "os", label: "Hệ điều hành" },
        { key: "state", label: "Trạng thái" },
        { key: "isPrimary", label: "Chính" },
        { key: "lanDungCuoi", label: "Dùng lần cuối" },
        { key: "thaoTac", label: "Thao tác" },
      ],
    };
  },
  computed: {
    stepUpTitle() {
      if (!this.pending) return "";
      if (this.pending.action === "lock") return "Xác nhận khoá thiết bị";
      if (this.pending.action === "revoke") return "Xác nhận hủy thiết bị";
      return "Xác nhận mở khoá thiết bị";
    },
  },
  methods: {
    showModal(targetUserId) {
      this.targetUserId = targetUserId;
      this.devices = [];
      this.$refs.modalThietBiUser.show();
      this.taiDanhSach();
    },
    taiDanhSach() {
      this.isLoading = true;
      this.$store
        .dispatch("common/listDeviceByUser", this.targetUserId)
        .then((res) => {
          this.isLoading = false;
          // Action resolve response.data (ServiceResponse camelCase): envelope .data = mảng device.
          this.devices = (res && res.data) || [];
        })
        .catch((error) => {
          this.isLoading = false;
          this.$toasted.global.showErrorMessage({
            message:
              (error && error.data && error.data.message) ||
              "Không tải được danh sách thiết bị",
          });
        });
    },
    onLock(device) {
      // Khoá device chính -> cảnh báo trước khi mở step-up.
      if (device.isPrimary) {
        this.$bvModal
          .msgBoxConfirm(
            "Đây là thiết bị chính. Khoá sẽ khiến người dùng không đăng nhập được bằng thiết bị này. Tiếp tục?",
            { title: "Cảnh báo", okTitle: "Tiếp tục", cancelTitle: "Huỷ", centered: true }
          )
          .then((ok) => {
            if (ok) this.openStepUp("lock", device.deviceId);
          });
        return;
      }
      this.openStepUp("lock", device.deviceId);
    },
    onUnlock(device) {
      this.openStepUp("unlock", device.deviceId);
    },
    onRevoke(device) {
      // Hủy thiết bị chính -> cảnh báo trước (BE tự chuyển primary sang TB Active khác, chặn nếu là TB cuối).
      if (device.isPrimary) {
        this.$bvModal
          .msgBoxConfirm(
            "Đây là thiết bị chính. Hủy sẽ chuyển thiết bị chính sang thiết bị hoạt động khác (nếu còn). Tiếp tục?",
            { title: "Cảnh báo", okTitle: "Tiếp tục", cancelTitle: "Huỷ", centered: true }
          )
          .then((ok) => {
            if (ok) this.openStepUp("revoke", device.deviceId);
          });
        return;
      }
      this.openStepUp("revoke", device.deviceId);
    },
    openStepUp(action, deviceId) {
      this.pending = { action, deviceId };
      this.otp = null;
      this.$refs.modalStepUp.show();
    },
    onStepUpHidden() {
      this.otp = null;
      this.pending = null;
    },
    confirmStepUp() {
      if (!this.pending || !this.otp) return;
      const act = this.pending.action;
      // revoke (UC7.5) endpoint riêng; lock/unlock chung khung step-up. Payload thống nhất 3 nhánh sau
      // khi bỏ adminDeviceId/authorizerDeviceId (fan-out BE, xem doc header).
      const action =
        act === "lock" ? "common/lockDevice" : act === "revoke" ? "common/revokeDevice" : "common/unlockDevice";
      // adminDeviceId/authorizerDeviceId omitted -> BE fan-out (xem doc header). pending.deviceId là
      // Device.DeviceId của TARGET (user bị lock/unlock/revoke), KHÔNG phải admin -> vẫn giữ.
      const payload = {
        userId: this.targetUserId,
        deviceId: this.pending.deviceId,
        twoFactorCode: this.otp,
      };
      const successMsg = {
        lock: "Khoá thiết bị thành công",
        unlock: "Mở khoá thiết bị thành công",
        revoke: "Hủy thiết bị thành công",
      }[act];
      this.isLoading = true;
      this.$store
        .dispatch(action, payload)
        .then((res) => {
          this.isLoading = false;
          // Action resolve response.data (camelCase): body ở res.succeeded.
          if (res && res.succeeded) {
            this.$toasted.clear();
            this.$toasted.global.showSuccessMessage({ message: successMsg });
            this.$refs.modalStepUp.hide();
            this.taiDanhSach();
          }
        })
        .catch((error) => {
          this.isLoading = false;
          this.$toasted.global.showErrorMessage({
            message:
              (error && error.data && error.data.message) ||
              "Thao tác thất bại, vui lòng kiểm tra mã xác thực.",
          });
        });
    },
    // Map enum DeviceState (BE): 0=ChoKichHoat, 1=Active, 2=Locked, 3=DaHuyKichHoat (UC7.5 soft-state).
    stateLabel(state) {
      return { 0: "Chờ kích hoạt", 1: "Đang hoạt động", 2: "Đã khoá", 3: "Đã hủy" }[state] || "Không rõ";
    },
    stateVariant(state) {
      return { 0: "warning", 1: "success", 2: "danger", 3: "secondary" }[state] || "secondary";
    },
    formatDate(value) {
      if (!value) return "";
      const d = new Date(value);
      return isNaN(d.getTime()) ? "" : d.toLocaleString("vi-VN");
    },
  },
};
</script>

<style lang="scss" scoped>
// Reskin OTP (Phase 4): overlay token + badge. Giữ state machine lock/unlock/revoke + step-up (KHÔNG đụng script).
.step-up-otp {
  font-family: var(--otp-sans);
  font-size: 14px;
  color: var(--otp-ink);
  .input {
    border: 1px solid var(--otp-border-strong);
    border-radius: var(--otp-radius-sm);
    height: 36px;
    &:focus { border-color: var(--otp-blue); box-shadow: var(--otp-ring); outline: none; }
  }
}

// Cột Thao tác: giãn khoảng cách giữa nút Khoá/Mở-khoá và Hủy thiết bị (trước dùng mr-1 quá sát -> dính nhau).
.thao-tac-buttons {
  gap: 0.5rem;
}

// Bảng thiết bị: header token, badge trạng thái map màu semantic theo variant (giữ stateVariant script).
::v-deep .table-thiet-bi {
  thead th {
    background-color: var(--otp-surface-2) !important;
    color: var(--otp-ink) !important;
    font-weight: 600;
    vertical-align: middle;
    border-color: var(--otp-border);
  }
  td {
    color: var(--otp-ink);
    border-color: var(--otp-border);
    vertical-align: middle;
  }
  .badge {
    font-size: 12px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;
    &.badge-success { background: var(--otp-success-bg); color: var(--otp-success); }
    &.badge-warning { background: var(--otp-warning-bg); color: var(--otp-warning); }
    &.badge-danger { background: var(--otp-danger-bg); color: var(--otp-danger); }
    &.badge-secondary { background: var(--otp-surface-2); color: var(--otp-muted); }
  }
}
</style>
